import { useMemo } from 'react';

import { captureMessage } from '@sentry/react';
import type { Address } from 'viem';
import type { QueryKey, UseQueryOptions } from '@soundxyz/graphql-react-query/reactQuery';
import { useQuery } from '@soundxyz/graphql-react-query/reactQuery';
import type { MintSchedule, SoundClient, V2_1MintSchedule, V2MintSchedule } from '@soundxyz/sdk';
import { merkleDropMinterV2Abi } from '@soundxyz/sdk/abi/merkle-drop-minter-v2';
import { soundEditionV1Abi } from '@soundxyz/sdk/abi/sound-edition-v1';
import { MINT_GAS_LIMIT_MULTIPLIER, minterAbiMap } from '@soundxyz/sdk/utils/constants';
import { scaleAmount } from '@soundxyz/sdk/utils/helpers';
import type { Maybe } from '@soundxyz/utils';
import { exhaustiveGuard, LazyPromise } from '@soundxyz/utils';
import { assertAddress } from '@soundxyz/utils/address';
import { NULL_ADDRESS } from '@soundxyz/utils/const';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import type { ContractType } from '@graphql/reactQuery';
import { GraphQLReactQuery } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useSoundSdk } from '@hooks/useSoundSdk';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { SoundContractInterfaceIds } from '@utils/constants';
import { assertIsHex } from '@utils/hex';
import { getMerkleProof } from '@utils/merkle';
import { isV2CompatibleMintSchedule } from '@utils/minters';
import { isInsufficientFundsError } from '@utils/viemErrors';
import { expectNormalGas, SDK_KEY } from './helpers';

export type ScheduleIds = Array<{ minterAddress: string; mintIds: number[] }>;
type ScheduleIdsStrictType = Array<{ minterAddress: Address; mintIds: number[] }>;

export function refetchSDK() {
  return GraphQLReactQuery.invalidateQueries({
    queryKey: [SDK_KEY],
  });
}

type EditionMintSchedules = {
  mintSchedules: MintSchedule[];
  activeMintSchedules: MintSchedule[];
};

const USE_SDK_SCHEDULE_IDS = !process.env.SKIP_SDK_SCHEDULE_IDS;

interface AccessToDropScheduleInfo {
  scheduleStartTime: number;
  scheduleEndTime: number;
  scheduleMintPaused: boolean;
  scheduleTotalMinted: number;
  scheduleMaxMintable: number;
  scheduleMaxMintablePerAccount: number;

  scheduleRangeInfo: {
    cutoffTime: number;
    maxMintableUpper: number;
    maxMintableLower: number;
  } | null;
  scheduleMerkleInfo: {
    merkleRoot: string;
  } | null;
}

function hasScheduleEligibility({
  editionAddress,
  mintSchedule,
  authUserAddress: authUserAddress,
  scheduleAccessDropInfo,
  auctionContractType,
}: {
  editionAddress: string;
  mintSchedule: Maybe<MintSchedule>;
  authUserAddress: string | undefined;
  scheduleAccessDropInfo: Maybe<AccessToDropScheduleInfo>;
  auctionContractType: Maybe<ContractType>;
}) {
  return {
    queryKey: [
      SDK_KEY,
      'has-schedule-access',
      editionAddress,
      authUserAddress,
      scheduleAccessDropInfo,
    ] as const,
    queryFn(): Promise<boolean> | boolean {
      if (auctionContractType === 'ARTIST') return !!authUserAddress;

      if (!authUserAddress || !scheduleAccessDropInfo || !mintSchedule) return false;

      const hasMerkleAccess = async () => {
        if (!scheduleAccessDropInfo) return false;

        const { scheduleMerkleInfo } = scheduleAccessDropInfo;

        if (!scheduleMerkleInfo) return true;

        assertIsHex(scheduleMerkleInfo.merkleRoot);
        assertAddress(authUserAddress);

        const proof = await getMerkleProof({
          merkleRoot: scheduleMerkleInfo.merkleRoot,
          userAddress: authUserAddress,
        });

        if (!proof?.length) {
          return false;
        } else {
          return true;
        }
      };
      return hasMerkleAccess();
    },
    enabled: !!mintSchedule && !!scheduleAccessDropInfo,
  };
}

/**
 * @note this hook is used to check if the user has access to the mint schedule regardless of how many editions they have minted
 */
export function useHasScheduleEligibility({
  mintSchedule,
  editionAddress,
  auctionContractType,
}: {
  mintSchedule: Maybe<MintSchedule>;
  editionAddress: string;
  auctionContractType: Maybe<ContractType>;
}) {
  const { authUserAddress } = useAuth();

  const scheduleAccessDropInfo = useMemo<AccessToDropScheduleInfo | null>(() => {
    if (!mintSchedule) return null;

    return getAccessToDropScheduleInfo(mintSchedule);
  }, [mintSchedule]);

  return useQuery(
    hasScheduleEligibility({
      editionAddress,
      mintSchedule,
      authUserAddress,
      scheduleAccessDropInfo,
      auctionContractType,
    }),
  );
}

async function hasAccessToDrop({
  scheduleStartTime,
  scheduleEndTime,
  scheduleMerkleInfo,
  scheduleMintPaused,
  scheduleRangeInfo,
  scheduleTotalMinted,
  scheduleMaxMintable,
  scheduleMaxMintablePerAccount,
  userAddress,
  editionTotalMintedPromise,
  editionMaxMintablePromise,
  mintedByUserFromSchedule,
}: AccessToDropScheduleInfo & {
  userAddress: string;

  editionTotalMintedPromise: Promise<bigint>;
  editionMaxMintablePromise: Promise<number>;
  mintedByUserFromSchedule: Promise<bigint>;
}): Promise<boolean> {
  // check valid mint time
  if (scheduleStartTime === scheduleEndTime || scheduleMintPaused) {
    return false;
  }

  // Checks for child minter custom logic
  if (scheduleRangeInfo) {
    const maxQty =
      scheduleStartTime < scheduleRangeInfo.cutoffTime
        ? scheduleRangeInfo.maxMintableUpper
        : scheduleRangeInfo.maxMintableLower;

    if (scheduleTotalMinted >= maxQty) {
      return false;
    }
  } else if (scheduleMerkleInfo) {
    assertIsHex(scheduleMerkleInfo.merkleRoot);
    assertAddress(userAddress);

    const proof = await getMerkleProof({
      merkleRoot: scheduleMerkleInfo.merkleRoot,
      userAddress,
    });

    if (!proof?.length) {
      return false;
    }
  }

  const [editionTotalMinted, editionMaxMintable] = await Promise.all([
    editionTotalMintedPromise,
    editionMaxMintablePromise,
  ]);

  const editionRemainingQty =
    editionMaxMintable - bigIntToNumber(editionTotalMinted, { strict: false });

  if (!editionRemainingQty) {
    return false;
  }

  // Get eligible quantity for the user on this mint schedule.
  const remainingForSchedule = scheduleMaxMintable - scheduleTotalMinted;

  const eligibleForUserOnSchedule =
    scheduleMaxMintablePerAccount - Number(await mintedByUserFromSchedule);
  const scheduleEligibleQty = Math.min(remainingForSchedule, eligibleForUserOnSchedule);

  // Return the minimum of the two. The number of tokens minted within the mint schedule
  // can never exceed the number of tokens available for the edition.
  return Math.max(0, Math.min(scheduleEligibleQty, editionRemainingQty)) !== 0;
}

function getAccessToDropScheduleInfo(mintSchedule: MintSchedule): AccessToDropScheduleInfo {
  return {
    scheduleStartTime: mintSchedule.startTime,
    scheduleEndTime: mintSchedule.endTime,
    scheduleMaxMintable:
      typeof mintSchedule.maxMintable === 'function'
        ? mintSchedule.maxMintable(mintSchedule.startTime)
        : mintSchedule.maxMintable,
    scheduleMaxMintablePerAccount: mintSchedule.maxMintablePerAccount,
    scheduleMerkleInfo:
      mintSchedule.mintType === 'MerkleDrop' ? { merkleRoot: mintSchedule.merkleRoot } : null,
    scheduleMintPaused: mintSchedule.mintPaused,
    scheduleTotalMinted: mintSchedule.totalMinted,
    scheduleRangeInfo:
      mintSchedule.mintType === 'RangeEdition'
        ? {
            cutoffTime: mintSchedule.cutoffTime,
            maxMintableLower: mintSchedule.maxMintableLower,
            maxMintableUpper: mintSchedule.maxMintableUpper,
          }
        : null,
  };
}

function hasAccessToScheduleQuery({
  editionAddress,
  auctionContractType,
  scheduleAccessDropInfo,
  chainId,
  authUserAddress: userAddress,
  provider,
  soundClientProvider,
  isFirstPhaseCompleted,
  mintSchedule,
}: {
  editionAddress: string;
  auctionContractType: ContractType | null;
  chainId: number;
  scheduleAccessDropInfo: AccessToDropScheduleInfo | null;
  authUserAddress: string | undefined;
  provider: ReturnType<typeof useConnectedAuth>['provider'];
  soundClientProvider: ReturnType<typeof useSoundSdk>['soundClientProvider'];
  isFirstPhaseCompleted: boolean;
  mintSchedule: Maybe<MintSchedule>;
}) {
  return {
    enabled: !!mintSchedule && !!scheduleAccessDropInfo,
    queryKey: [
      SDK_KEY,
      'has-schedule-access',
      editionAddress,
      userAddress,
      scheduleAccessDropInfo,
      chainId.toString(),
    ] as const,
    queryFn(): Promise<boolean> | boolean {
      if (isFirstPhaseCompleted) return false;

      if (auctionContractType === 'ARTIST') return !!userAddress;

      if (!userAddress || !scheduleAccessDropInfo || !mintSchedule) return false;

      // Use react query orchestration to synchronize these calls within multiple calls
      const sharedPromises = GraphQLReactQuery.fetchQuery({
        queryKey: ['edition-info', editionAddress, userAddress, chainId.toString()],
        queryFn() {
          const chainProvider = provider(chainId);

          assertIsHex(editionAddress);

          const editionMaxMintablePromise = LazyPromise(() => {
            return chainProvider.readContract({
              abi: soundEditionV1Abi,
              address: editionAddress,
              functionName: 'editionMaxMintable',
            });
          });

          const editionTotalMintedPromise = LazyPromise(() => {
            return chainProvider.readContract({
              abi: soundEditionV1Abi,
              address: editionAddress,
              functionName: 'totalMinted',
            });
          });

          return {
            editionMaxMintablePromise,
            editionTotalMintedPromise,
          };
        },
      });

      const mintedByUserFromSchedule = LazyPromise(async () => {
        const soundProvider = soundClientProvider(chainId);
        const chainProvider = provider(chainId);

        assertAddress(userAddress);
        assertAddress(editionAddress);

        return mintSchedule.interfaceId === SoundContractInterfaceIds.IMerkleDropMinterV2 ||
          mintSchedule.interfaceId === SoundContractInterfaceIds.IMerkleDropMinterV2_1
          ? chainProvider.readContract({
              abi: merkleDropMinterV2Abi,
              address: mintSchedule.minterAddress,
              functionName: 'mintCount',
              args: [mintSchedule.editionAddress, mintSchedule.mintId, userAddress],
            })
          : soundProvider.edition.numberMinted({
              editionAddress,
              userAddress,
            });
      });

      return hasAccessToDrop({
        ...scheduleAccessDropInfo,
        userAddress,
        editionMaxMintablePromise: LazyPromise(() =>
          sharedPromises.then(v => v.editionMaxMintablePromise),
        ),
        mintedByUserFromSchedule,
        editionTotalMintedPromise: LazyPromise(() =>
          sharedPromises.then(v => v.editionTotalMintedPromise),
        ),
      });
    },
  } satisfies UseQueryOptions;
}

export function useHasAccessToSchedule({
  mintSchedule,
  editionAddress,
  auctionContractType,
  chainId,
  isFirstPhaseCompleted,
}: {
  mintSchedule: Maybe<MintSchedule>;
  editionAddress: string;
  auctionContractType: ContractType | null;
  chainId: number;
  isFirstPhaseCompleted: boolean;
}) {
  const { authUserAddress } = useAuth();
  const { provider } = useConnectedAuth();
  const { soundClientProvider } = useSoundSdk();

  const scheduleAccessDropInfo = useMemo<AccessToDropScheduleInfo | null>(() => {
    if (!mintSchedule) return null;

    return getAccessToDropScheduleInfo(mintSchedule);
  }, [mintSchedule]);

  return useQuery(
    hasAccessToScheduleQuery({
      chainId,
      editionAddress,
      scheduleAccessDropInfo,
      auctionContractType,
      authUserAddress,
      provider,
      soundClientProvider,
      isFirstPhaseCompleted,
      mintSchedule,
    }),
  );
}

export function useEditionMintSchedules(
  {
    editionAddress,
    scheduleIds,
    chainId,
    auctionContractType,
  }: {
    editionAddress: string;
    scheduleIds: Maybe<ScheduleIds>;
    chainId: number;
    auctionContractType: ContractType | null;
  },
  options?: Omit<
    UseQueryOptions<
      Pick<EditionMintSchedules, 'mintSchedules'>,
      Error,
      EditionMintSchedules,
      readonly unknown[]
    >,
    'queryKey' | 'queryFn' | 'select'
  >,
) {
  const { soundClientProvider } = useSoundSdk();

  const result = useQuery({
    queryKey: [
      SDK_KEY,
      'edition-mint-schedules',
      USE_SDK_SCHEDULE_IDS && scheduleIds,
      editionAddress,
      chainId.toString(),
    ] as QueryKey,
    async queryFn() {
      const soundProvider = soundClientProvider(chainId);

      assertAddress(editionAddress);

      const { schedules: mintSchedules } = await soundProvider.edition.mintSchedules({
        scheduleIds: scheduleIds as ScheduleIdsStrictType,
        editionAddress,
      });

      return {
        mintSchedules: mintSchedules.map((schedule, index) => {
          const nextSchedule = mintSchedules[index + 1];

          return {
            ...schedule,
            startTime:
              nextSchedule && nextSchedule.startTime === schedule.startTime + 1
                ? nextSchedule.startTime
                : schedule.startTime,
          };
        }),
      };
    },
    select({ mintSchedules }) {
      const now = Math.floor(Date.now() / 1000);

      const activeMintSchedules = mintSchedules.filter(mintSchedule => {
        return (
          mintSchedule.startTime <= now && mintSchedule.endTime > now && !mintSchedule.mintPaused
        );
      });

      return {
        mintSchedules,
        activeMintSchedules,
      };
    },
    ...options,
    enabled: (options?.enabled ?? true) && auctionContractType === 'EDITION',
  });

  return result;
}

type EditionInfo = Awaited<ReturnType<SoundClient['edition']['info']>['contract']['info']>;

export function useEditionInfo<QueryData = EditionInfo>(
  {
    editionAddress,
    chainId,
    auctionContractType,
  }: { editionAddress: string; chainId: number; auctionContractType: ContractType | null },
  options?: Omit<UseQueryOptions<EditionInfo, Error, QueryData, string[]>, 'queryKey' | 'queryFn'>,
) {
  const { soundClientProvider } = useSoundSdk();

  const result = useQuery({
    queryKey: [SDK_KEY, 'edition-info', editionAddress, chainId.toString()],
    queryFn() {
      const soundProvider = soundClientProvider(chainId);
      return soundProvider.edition.info({
        contractAddress: editionAddress,
      }).contract.info;
    },
    ...options,
    enabled: (options?.enabled ?? true) && auctionContractType === 'EDITION',
  });

  return result;
}

function eligibleQuantityQueryKey({
  mintSchedule,
  userAddress,
  editionAddress,
}: {
  mintSchedule: Maybe<MintSchedule>;
  userAddress: Maybe<string>;
  editionAddress: Maybe<string>;
}): QueryKey {
  return [
    SDK_KEY,
    'edition-eligible-quantity',
    editionAddress,
    mintSchedule?.minterAddress,
    mintSchedule?.mintId?.toString(),
    userAddress,
  ] as const;
}

export function useEditionEligibleQuantity<QueryData = bigint | null>(
  {
    mintSchedule,
    chainId,
    editionAddress,
    giftTo,
  }: {
    mintSchedule: Maybe<MintSchedule>;
    chainId: number;
    editionAddress: string;
    giftTo: Address | null;
  },
  options?: Omit<
    UseQueryOptions<bigint | null, Error, QueryData, ReturnType<typeof eligibleQuantityQueryKey>>,
    'queryKey' | 'queryFn'
  >,
) {
  const { authUserAddress: userAddress } = useAuth();
  const { soundClientProvider } = useSoundSdk();

  const toAddress = giftTo ?? userAddress;

  const getEligibleQuantity = useStableCallback(
    async ({
      mintSchedule,
      userAddress,
      editionAddress,
    }: {
      mintSchedule: Maybe<MintSchedule>;
      userAddress: Maybe<string>;
      editionAddress: Maybe<string>;
    }) => {
      if (!mintSchedule || !userAddress || !editionAddress) return 0n;

      assertAddress(userAddress);

      const eligibleQuantity = await soundClientProvider(chainId).edition.eligibleQuantity({
        mintSchedule,
        userAddress,
      });

      GraphQLReactQuery.setQueryData(
        eligibleQuantityQueryKey({ mintSchedule, userAddress, editionAddress }),
        eligibleQuantity,
      );

      return eligibleQuantity;
    },
  );

  const result = useQuery({
    queryKey: eligibleQuantityQueryKey({ mintSchedule, userAddress: toAddress, editionAddress }),
    queryFn() {
      return getEligibleQuantity({
        mintSchedule,
        userAddress: toAddress,
        editionAddress,
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && !!mintSchedule && !!toAddress,
  });

  return {
    ...result,
    getEligibleQuantity,
  };
}

export function useEstimateMintGas({
  mintSchedule,
  chainId,
  quantity,
  editionAddress,
  canCollectSafely,
}: {
  mintSchedule: MintSchedule;
  chainId: number;
  quantity: number;
  editionAddress: string;
  canCollectSafely: boolean;
}) {
  const { authUserAddress } = useAuth();
  const { provider } = useConnectedAuth();

  const logError = useLogError();

  return useQuery({
    staleTime: Infinity,
    cacheTime: Infinity,
    queryKey: [
      SDK_KEY,
      'mint-estimate',
      editionAddress,
      quantity,
      authUserAddress,
      chainId,
      mintSchedule.minterAddress,
      mintSchedule.mintId.toString(),
    ],
    async queryFn() {
      if (!authUserAddress) return null;

      try {
        const estimateProvider = provider(chainId);

        const interfaceId = mintSchedule.interfaceId;

        switch (interfaceId) {
          case SoundContractInterfaceIds.IRangeEditionMinter:
          case SoundContractInterfaceIds.IRangeEditionMinterV2:
          case SoundContractInterfaceIds.IRangeEditionMinterV2_1: {
            const gasEstimate = await estimateProvider.estimateContractGas({
              abi: minterAbiMap[SoundContractInterfaceIds.IRangeEditionMinter],
              account: authUserAddress,
              address: mintSchedule.minterAddress,
              functionName: 'mint',
              args: [mintSchedule.editionAddress, mintSchedule.mintId, quantity, NULL_ADDRESS],
              value:
                (mintSchedule.price + mintSchedule.platformPerTokenFee) * BigInt(quantity) +
                mintSchedule.platformPerTransactionFee,
            });

            // Add a buffer to the gas estimate to account for node provider estimate variance.
            const gasLimit = expectNormalGas({
              estimation: scaleAmount({
                amount: gasEstimate,
                multiplier: MINT_GAS_LIMIT_MULTIPLIER,
              }),
              editionAddress: mintSchedule.editionAddress,
              chainId,
              quantity,
              loggingExtra: {
                mintSchedule,
              },
            });

            return {
              gasLimit,
            };
          }
          case SoundContractInterfaceIds.IMerkleDropMinter:
          case SoundContractInterfaceIds.IMerkleDropMinterV2:
          case SoundContractInterfaceIds.IMerkleDropMinterV2_1: {
            const params = {
              address: mintSchedule.minterAddress,
              functionName: 'mintInfo',
              args: [mintSchedule.editionAddress, mintSchedule.mintId],
            } as const;
            const { merkleRootHash: merkleRoot } = await (interfaceId ===
            SoundContractInterfaceIds.IMerkleDropMinter
              ? estimateProvider.readContract({
                  ...params,
                  abi: minterAbiMap[interfaceId],
                })
              : interfaceId === SoundContractInterfaceIds.IMerkleDropMinterV2
              ? estimateProvider.readContract({
                  ...params,
                  abi: minterAbiMap[interfaceId],
                })
              : estimateProvider.readContract({
                  ...params,
                  abi: minterAbiMap[interfaceId],
                }));

            const proof = await getMerkleProof({
              merkleRoot,
              userAddress: authUserAddress,
            });

            if (!proof?.length) return null;

            const mintArgs = [
              mintSchedule.editionAddress,
              mintSchedule.mintId,
              quantity,
              proof,
              NULL_ADDRESS,
            ] as const;

            // Add a buffer to the gas estimate to account for node provider estimate variance.
            const gasEstimate = await estimateProvider.estimateContractGas({
              abi: minterAbiMap[SoundContractInterfaceIds.IMerkleDropMinter],
              account: authUserAddress,
              address: mintSchedule.minterAddress,
              functionName: 'mint',
              args: mintArgs,
              value:
                (mintSchedule.price + mintSchedule.platformPerTokenFee) * BigInt(quantity) +
                mintSchedule.platformPerTransactionFee,
            });

            const gasLimit = expectNormalGas({
              estimation: scaleAmount({
                amount: gasEstimate,
                multiplier: MINT_GAS_LIMIT_MULTIPLIER,
              }),
              editionAddress: mintSchedule.editionAddress,
              chainId,
              quantity,
              loggingExtra: {
                mintSchedule,
              },
            });

            return {
              gasLimit,
            };
          }
          default: {
            captureMessage('Unexpected missing minter handling for mint estimate', {
              extra: {
                mintSchedule,
              },
              level: 'error',
            });
            exhaustiveGuard(interfaceId);
          }
        }
      } catch (error) {
        // This means the wallet simply doesn't have funds, which is normal
        if (isInsufficientFundsError(error)) {
          return {
            gasLimit: undefined,
          };
        }

        logError({
          error,
          action: 'estimate-gas',
          errorType: 'UNKNOWN',
          isArtistOnlyPage: false,
          level: 'error',
          message: 'Unexpected error while trying to estimate mint gas',
          pillar: 'MINTING',
          unindexedExtra: {
            mintSchedule,
          },
        });

        return {
          gasLimit: undefined,
        };
      }
    },
    enabled: !!authUserAddress && !!quantity && canCollectSafely,
  });
}

export function useDropConfigMintersV2Schedules({
  editionAddress,
  chainId,
}: {
  editionAddress: string;
  chainId: number;
}) {
  const { soundClientProvider } = useSoundSdk();

  const { data, isLoading } = useQuery({
    queryKey: [
      SDK_KEY,
      'drop-config-mint-schedules',
      editionAddress,
      chainId.toString(),
    ] as QueryKey,
    refetchOnWindowFocus: false,
    async queryFn() {
      const clientProvider = soundClientProvider(chainId);

      assertAddress(editionAddress);
      const [mintSchedules, editionInfo] = await Promise.all([
        await clientProvider.edition
          .mintSchedules({
            editionAddress,
          })
          .then(v =>
            v.schedules.filter((s): s is V2MintSchedule | V2_1MintSchedule =>
              isV2CompatibleMintSchedule(s),
            ),
          ),
        clientProvider.edition.info({ contractAddress: editionAddress }).contract.info,
      ]);

      return {
        mintSchedules,
        totalMinted: editionInfo.totalMinted,
      };
    },
  });

  return { data, isLoading };
}

export function getMintPrice({
  mintSchedule,
  quantity,
}: {
  mintSchedule: MintSchedule;
  quantity: number;
}) {
  const unitPrice = mintSchedule.price;
  const subTotal = unitPrice * BigInt(quantity);

  const platformFee =
    mintSchedule.platformPerTokenFee * BigInt(quantity) + mintSchedule.platformPerTransactionFee;

  const total = subTotal + platformFee;

  return {
    total,
    subTotal,
    unitPrice,
    platformFee,
  };
}
